import React, { useRef } from "react";
import { Drawer, Row, Col, Space, Tag, Divider, Button, Alert, Card } from "antd";
import { UserOutlined, PhoneOutlined, PrinterOutlined } from "@ant-design/icons";
import moment from "moment-jalaali";
import { useReactToPrint } from "react-to-print";
import SafeHtml from "@general/safeHtml";
import PrintPaper from "@general/printPaper";
import { SERVICE_TYPES, SERVICE_STATUSES } from "@staticData/services";

function AdminServiceTableDetails({ open, onClose, service, windowSize }) {
  const contentRef = useRef(null);

  const reactToPrintFn = useReactToPrint({
    contentRef,
    pageStyle: `
      @font-face {
        font-family: "IRANSansXFaNum";
        src: url("${window.location.origin}/fonts/woff2/IRANSansXFaNum-Regular.woff2") format("woff2");
      }
      .contract-wrapper,
      .contract-wrapper * {
        font-family: "IRANSansXFaNum" !important;
      }
    `,
  });

  if (!service) return null;

  const {
    serviceName,
    serviceType,
    status,
    amount,
    createdAt,
    startAt,
    endAt,
    description,
    contract,
    user,
    packageName,
    packageDays,
    packageSessions,
    meetingRoomName,
    reserve,
    workAt,
    startTime,
    endTime,
    capacity,
  } = service;

  const statusConfig = SERVICE_STATUSES[status] || {
    label: status,
    color: "default",
  };
  const typeLabel = SERVICE_TYPES[serviceType] || serviceType;

  const formatDate = (date, withTime = false) => {
    if (!date) return "-";
    return withTime
      ? moment(date).format("jYYYY/jMM/jDD | HH:mm")
      : moment(date).format("jYYYY/jMM/jDD");
  };

  const isMeetingOrConsultation =
    serviceType === "MEETING_ROOM" || serviceType === "CONSULTATION";

  return (
    <Drawer
      title="جزییات سرویس کاربر"
      placement="left"
      onClose={onClose}
      open={open}
      width={windowSize > 768 ? 680 : "100%"}
    >
      {/* User Information Card */}
      <Card
        size="small"
        title="اطلاعات کاربر"
        style={{ marginBottom: 20, backgroundColor: "#fafafa" }}
      >
        <Row gutter={[16, 12]}>
          <Col sm={12} xs={24}>
            <Space>
              <UserOutlined style={{ color: "#1890ff" }} />
              <span style={{ color: "#888" }}>نام کاربر:</span>
              <strong>{user?.name || "-"}</strong>
            </Space>
          </Col>
          <Col sm={12} xs={24}>
            <Space>
              <PhoneOutlined style={{ color: "#52c41a" }} />
              <span style={{ color: "#888" }}>شماره موبایل:</span>
              <span>{user?.mobile || "-"}</span>
            </Space>
          </Col>
        </Row>
      </Card>

      {/* Service Summary */}
      <Row gutter={[16, 16]}>
        <Col span={24}>
          <Space orientation="horizontal" size="middle" wrap>
            <h3 style={{ margin: 0 }}>{serviceName}</h3>
            <Tag color={statusConfig.color}>{statusConfig.label}</Tag>
            <Tag color="cyan">{typeLabel}</Tag>
          </Space>
        </Col>

        <Col sm={12} xs={24}>
          <Space>
            <span style={{ color: "#888" }}>مبلغ:</span>
            <strong>{amount ? amount.toLocaleString() : 0} ریال</strong>
          </Space>
        </Col>

        <Col sm={12} xs={24}>
          <Space>
            <span style={{ color: "#888" }}>تاریخ خرید:</span>
            <span>{formatDate(createdAt)}</span>
          </Space>
        </Col>

        <Col sm={12} xs={24}>
          <Space>
            <span style={{ color: "#888" }}>تاریخ شروع:</span>
            <span>{formatDate(startAt, isMeetingOrConsultation)}</span>
          </Space>
        </Col>

        <Col sm={12} xs={24}>
          <Space>
            <span style={{ color: "#888" }}>تاریخ پایان:</span>
            <span>{formatDate(endAt, isMeetingOrConsultation)}</span>
          </Space>
        </Col>

        {packageName && (
          <Col sm={12} xs={24}>
            <Space>
              <span style={{ color: "#888" }}>نام پکیج:</span>
              <span>{packageName}</span>
            </Space>
          </Col>
        )}

        {packageDays !== null && packageDays !== undefined && (
          <Col sm={12} xs={24}>
            <Space>
              <span style={{ color: "#888" }}>مدت پکیج:</span>
              <span>{packageDays} روز</span>
            </Space>
          </Col>
        )}

        {packageSessions !== null && packageSessions !== undefined && (
          <Col sm={12} xs={24}>
            <Space>
              <span style={{ color: "#888" }}>تعداد جلسات:</span>
              <span>{packageSessions} جلسه</span>
            </Space>
          </Col>
        )}

        {meetingRoomName && (
          <Col sm={12} xs={24}>
            <Space>
              <span style={{ color: "#888" }}>مکان / اتاق جلسه:</span>
              <span>{meetingRoomName}</span>
            </Space>
          </Col>
        )}

        {workAt && (
          <Col sm={12} xs={24}>
            <Space>
              <span style={{ color: "#888" }}>تاریخ حضور در روز تعطیل:</span>
              <span>{formatDate(workAt)}</span>
            </Space>
          </Col>
        )}

        {(startTime || endTime) && (
          <Col sm={12} xs={24}>
            <Space>
              <span style={{ color: "#888" }}>ساعت مجاز:</span>
              <span>
                {startTime || "-"} تا {endTime || "-"}
              </span>
            </Space>
          </Col>
        )}

        {capacity !== null && capacity !== undefined && (
          <Col sm={12} xs={24}>
            <Space>
              <span style={{ color: "#888" }}>ظرفیت دوره/رویداد:</span>
              <span>{capacity} نفر</span>
            </Space>
          </Col>
        )}

        {reserve && (
          <Col span={24}>
            <Alert
              message="اطلاعات قرار ملاقات / رزرو"
              description={`جلسه تنظیم‌شده: از ${formatDate(
                reserve.startAt,
                true
              )} تا ${formatDate(reserve.endAt, true)}`}
              type="info"
              showIcon
            />
          </Col>
        )}

        {description && (
          <Col span={24}>
            <div style={{ color: "#888", marginBottom: 6 }}>توضیحات:</div>
            <div
              style={{
                backgroundColor: "#fafafa",
                padding: "10px 14px",
                borderRadius: 6,
                border: "1px solid #f0f0f0",
                whiteSpace: "pre-line",
              }}
            >
              <SafeHtml content={description} />
            </div>
          </Col>
        )}
      </Row>

      <Divider />

      {contract && (
        <Space direction="vertical" style={{ width: "100%" }}>
          <Button
            type="primary"
            icon={<PrinterOutlined />}
            onClick={reactToPrintFn}
          >
            چاپ قرارداد / تعهدنامه
          </Button>
          <div style={{ display: "none" }}>
            <PrintPaper html={contract} ref={contentRef}>
              <span>
                ثبت شده در تاریخ {moment(createdAt).format("jYYYY/jMM/jDD")}
              </span>
            </PrintPaper>
          </div>
        </Space>
      )}
    </Drawer>
  );
}

export default AdminServiceTableDetails;
